"use client";

import Link from "next/link";
import { Authenticated, AuthLoading, Unauthenticated } from "convex/react";
import { ArrowRight, Loader2 } from "@/components/icons";
import { getProductTypeBySlug } from "@/lib/generated/product-types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  buildInventoryCreateUrl,
  buildInventorySignInUrl,
  buildInventorySignUpUrl,
} from "@/lib/auth/inventory-create-flow";
import { trackToolUsed } from "@/lib/analytics/tool-events";

function trackGradeCta(productTypeSlug: string): void {
  trackToolUsed({ tool: "grade", tool_step: "cta_clicked", productTypeSlug });
}

interface ConversionCTAProps {
  productTypeName: string;
  sessionId: string;
  detectedProductType: string;
}

export function ConversionCTA({
  productTypeName,
  sessionId,
  detectedProductType,
}: ConversionCTAProps) {
  const flowOptions = {
    productSlug: detectedProductType,
    intent: "sell" as const,
    gradingSession: sessionId,
  };
  const listedName =
    getProductTypeBySlug(detectedProductType)?.pluralDisplayName ??
    productTypeName;

  return (
    <Card className="bg-muted/40">
      <CardContent className="space-y-4 p-6">
        <h3 className="text-center text-lg font-semibold">Ready to sell?</h3>
        <AuthLoading>
          <Button variant="primary" disabled className="w-full">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading
          </Button>
        </AuthLoading>
        <Authenticated>
          <Button variant="primary" asChild className="w-full">
            <Link
              href={buildInventoryCreateUrl(flowOptions)}
              onClick={() => trackGradeCta(detectedProductType)}
            >
              List your {listedName}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </Authenticated>
        <Unauthenticated>
          <SignUpPrompt flowOptions={flowOptions} />
        </Unauthenticated>
      </CardContent>
    </Card>
  );
}

function SignUpPrompt({
  flowOptions,
}: {
  flowOptions: Parameters<typeof buildInventorySignUpUrl>[0];
}) {
  return (
    <>
      <Button variant="primary" asChild className="w-full">
        <a
          href={buildInventorySignUpUrl(flowOptions)}
          onClick={() => trackGradeCta(flowOptions.productSlug)}
        >
          Create an account to list
          <ArrowRight className="h-4 w-4" />
        </a>
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        Already have an account?{" "}
        <a
          href={buildInventorySignInUrl(flowOptions)}
          onClick={() => trackGradeCta(flowOptions.productSlug)}
          className="text-link hover:text-link-hover hover:underline"
        >
          Sign in
        </a>
      </p>
    </>
  );
}
